import { CONSONANT_MAP, INITIAL_CONSONANT_LETTERS, MIDDLE_VOWEL_LETTERS, FINAL_CONSONANT_LETTERS, } from "./constants.js";
export class Hangeul {
    static INITIAL_CONSONANT_LETTERS = INITIAL_CONSONANT_LETTERS;
    static MIDDLE_VOWEL_LETTERS = MIDDLE_VOWEL_LETTERS;
    static FINAL_CONSONANT_LETTERS = FINAL_CONSONANT_LETTERS;
    static CONSONANT_MAP = CONSONANT_MAP;
    static decompose(text, opts) {
        const results = [];
        for (const char of text) {
            const code = char.charCodeAt(0);
            // 반드시 초성과 중성이 포함되어 있는 범위 ('가'~'힣')
            if (code >= 0xac00 && code <= 0xd7a3) {
                const syllableIndex = code - 0xac00;
                const first = Math.floor(syllableIndex / (21 * 28));
                const middle = Math.floor((syllableIndex % (21 * 28)) / 28);
                const last = syllableIndex % 28;
                results.push(...this.decomposeConsonant(Hangeul.INITIAL_CONSONANT_LETTERS[first]));
                if (opts?.firstConsonantOnly)
                    continue; // 초성만 분해할 경우 중성, 종성은 무시
                results.push(...this.decomposeConsonant(Hangeul.MIDDLE_VOWEL_LETTERS[middle]));
                if (last) {
                    results.push(...this.decomposeConsonant(Hangeul.FINAL_CONSONANT_LETTERS[last]));
                }
            }
            else {
                // 자음, 모음 단독으로 쓰인 경우도 포함
                results.push(...this.decomposeConsonant(char));
            }
        }
        return results;
    }
    static decomposeByChar(text, opts) {
        const results = [];
        for (const char of text) {
            const code = char.charCodeAt(0);
            const charResult = [];
            if (code >= 0xac00 && code <= 0xd7a3) {
                const syllableIndex = code - 0xac00;
                const first = Math.floor(syllableIndex / (21 * 28));
                const middle = Math.floor((syllableIndex % (21 * 28)) / 28);
                const last = syllableIndex % 28;
                charResult.push(...this.decomposeConsonant(Hangeul.INITIAL_CONSONANT_LETTERS[first]));
                if (!opts?.firstConsonantOnly) {
                    charResult.push(...this.decomposeConsonant(Hangeul.MIDDLE_VOWEL_LETTERS[middle]));
                    if (last) {
                        charResult.push(...this.decomposeConsonant(Hangeul.FINAL_CONSONANT_LETTERS[last]));
                    }
                }
            }
            else {
                charResult.push(...this.decomposeConsonant(char));
            }
            results.push(charResult);
        }
        return results;
    }
    static decomposeConsonant(char) {
        if (char.length !== 1) {
            throw new Error("Input must be a single character");
        }
        if (this.CONSONANT_MAP[char]) {
            return this.CONSONANT_MAP[char];
        }
        return [char];
    }
    static compare(a, b, opts) {
        const aDecomposed = Hangeul.decompose(a, opts);
        const bDecomposed = Hangeul.decompose(b);
        if (aDecomposed.length !== bDecomposed.length)
            return false;
        for (let i = 0; i < aDecomposed.length; i++) {
            if (aDecomposed[i] !== bDecomposed[i])
                return false;
        }
        return true;
    }
    static startsWith(text, searchText, opts) {
        const textDecomposed = Hangeul.decompose(text, opts);
        const searchTextDecomposed = Hangeul.decompose(searchText);
        if (searchTextDecomposed.length > textDecomposed.length)
            return false;
        for (let i = 0; i < searchTextDecomposed.length; i++) {
            if (searchTextDecomposed[i] !== textDecomposed[i])
                return false;
        }
        return true;
    }
    static endsWith(text, searchText, opts) {
        const textDecomposed = Hangeul.decompose(text, opts);
        const searchTextDecomposed = Hangeul.decompose(searchText);
        if (searchTextDecomposed.length > textDecomposed.length)
            return false;
        for (let i = 0; i < searchTextDecomposed.length; i++) {
            if (searchTextDecomposed[searchTextDecomposed.length - 1 - i] !==
                textDecomposed[textDecomposed.length - 1 - i])
                return false;
        }
        return true;
    }
    // NOTE: '박스', 'ㄳ'처럼 종성부터 매칭되는 경우를 방지하기 위해 각 문자에 대한 시작 인덱스를 기준으로 비교한다.
    // 예: 'ㅂ', 'ㅏ', 'ㄱ', 'ㅅ', 'ㅡ'의 'ㅂ'부터 비교하여 매칭에 실패했다면, 다음은 'ㅅ'부터 비교를 시도한다.
    static includes(text, searchText, opts) {
        const textDecomposed = Hangeul.decomposeByChar(text, opts);
        const searchTextDecomposed = Hangeul.decompose(searchText);
        if (searchTextDecomposed.length === 0)
            return true;
        const indices = [0];
        textDecomposed.forEach(decomposed => {
            indices.push((indices.at(-1) ?? 0) + decomposed.length);
        });
        indices.pop();
        const textDecomposedFlat = textDecomposed.flat();
        let idx = 0;
        while (idx < indices.length) {
            const startIdx = indices[idx++];
            if (textDecomposedFlat.length - startIdx < searchTextDecomposed.length)
                break;
            if (textDecomposedFlat.slice(startIdx, startIdx + searchTextDecomposed.length).every((v, i) => {
                if (opts?.caseSensitive) {
                    return v === searchTextDecomposed[i];
                }
                else {
                    return v.toLowerCase() === searchTextDecomposed[i].toLowerCase();
                }
            }))
                return true;
        }
        return false;
    }
}
export default Hangeul;
